import * as React from "react";
import { LandscapeEngine } from "./engine";
import { FALLBACK_GRADIENTS, isLand, JOURNEY, type Land } from "./lands";
import type { FromRenderer, ToRenderer, Viewport } from "./protocol";

/**
 * Cinematic land backgrounds behind every page.
 *
 * Pages declare what they want: `useLandBackground("forest")` (or a list,
 * cycled slowly), and long pages mark sections with `useLandSection(land)` /
 * <LandSection>. The section crossing the middle of the viewport wins, picked
 * by an IntersectionObserver, so scrolling itself runs no code here. The
 * controller only posts tiny messages; all rendering happens in a worker on an
 * OffscreenCanvas (main-thread WebGL or a static CSS gradient as fallbacks).
 */

/** Device pixel ratio cap: the scene is soft by design, extra pixels only cost GPU time. */
const MAX_DPR = 1.25;

interface Sink {
  lands(lands: readonly Land[]): void;
  viewport(viewport: Viewport): void;
  visible(visible: boolean): void;
  reducedMotion(reduced: boolean): void;
  dispose(): void;
}

class LandscapeController {
  private sink: Sink | null = null;
  private readonly declared = new Map<string, readonly Land[]>();
  private readonly sections = new Map<Element, Land>();
  private readonly inBand: Element[] = [];
  private activeSection: Element | null = null;
  private current = "";
  private observer: IntersectionObserver | null = null;
  private readonly override: readonly Land[] | null = devOverride();

  attach(sink: Sink | null) {
    this.sink = sink;
    this.current = "";
    this.update();
  }

  declare(id: string, lands: readonly Land[]) {
    this.declared.delete(id);
    this.declared.set(id, lands);
    this.update();
  }

  undeclare(id: string) {
    this.declared.delete(id);
    this.update();
  }

  observe(element: Element, land: Land): () => void {
    this.sections.set(element, land);
    this.bandObserver()?.observe(element);
    return () => {
      this.observer?.unobserve(element);
      this.sections.delete(element);
      this.leave(element);
      if (this.activeSection === element) this.activeSection = this.inBand.at(-1) ?? null;
      this.update();
    };
  }

  /** Only sections overlapping the middle 10% of the viewport count as "in band". */
  private bandObserver(): IntersectionObserver | null {
    if (this.observer || typeof IntersectionObserver === "undefined") return this.observer;
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.leave(entry.target);
            this.inBand.push(entry.target);
          } else {
            this.leave(entry.target);
          }
        }
        // Keep the last section while scrolling through gaps between sections.
        this.activeSection = this.inBand.at(-1) ?? this.activeSection;
        this.update();
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 },
    );
    return this.observer;
  }

  private leave(element: Element) {
    const index = this.inBand.indexOf(element);
    if (index >= 0) this.inBand.splice(index, 1);
  }

  private resolve(): readonly Land[] {
    if (this.override) return this.override;
    const section = this.activeSection ? this.sections.get(this.activeSection) : undefined;
    if (section) return [section];
    const pages = [...this.declared.values()];
    return pages.at(-1) ?? JOURNEY;
  }

  private update() {
    const lands = this.resolve();
    const key = lands.join();
    if (key === this.current || !this.sink) return;
    this.current = key;
    this.sink.lands(lands);
  }

  currentLands(): readonly Land[] {
    return this.resolve();
  }
}

const LandscapeContext = React.createContext<LandscapeController | null>(null);

export function LandscapeProvider({ children }: { children: React.ReactNode }) {
  const [controller] = React.useState(() => new LandscapeController());
  return (
    <LandscapeContext.Provider value={controller}>
      <LandscapeCanvas controller={controller} />
      {children}
    </LandscapeContext.Provider>
  );
}

/** Page-level land(s). Several lands are cycled through; `null` falls back to the journey. */
export function useLandBackground(lands: Land | readonly Land[] | null | undefined) {
  const controller = React.useContext(LandscapeContext);
  const id = React.useId();
  const list = lands == null ? JOURNEY : typeof lands === "string" ? [lands] : lands;
  const key = list.join();
  React.useEffect(() => {
    if (!controller) return;
    controller.declare(id, key.split(",").filter(isLand));
    return () => controller.undeclare(id);
  }, [controller, id, key]);
}

/** Marks an element as a section painted in `land` while it crosses the viewport's middle. */
export function useLandSection<T extends Element>(land: Land): React.RefCallback<T> {
  const controller = React.useContext(LandscapeContext);
  return React.useCallback(
    (element: T | null) => {
      if (!element || !controller) return;
      return controller.observe(element, land);
    },
    [controller, land],
  );
}

export function LandSection({
  land,
  ...props
}: { land: Land } & React.HTMLAttributes<HTMLDivElement>) {
  return <div ref={useLandSection<HTMLDivElement>(land)} {...props} />;
}

function LandscapeCanvas({ controller }: { controller: LandscapeController }) {
  const host = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const container = host.current;
    if (!container) return;
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sink = createSink(container, controller.currentLands(), motionQuery.matches);
    controller.attach(sink);

    let resizeTimer = 0;
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => sink.viewport(readViewport()), 150);
    };
    const onVisibility = () => sink.visible(document.visibilityState === "visible");
    const onMotion = () => sink.reducedMotion(motionQuery.matches);
    window.addEventListener("resize", onResize, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    motionQuery.addEventListener("change", onMotion);
    return () => {
      window.clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
      motionQuery.removeEventListener("change", onMotion);
      controller.attach(null);
      sink.dispose();
    };
  }, [controller]);

  return (
    <div
      ref={host}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
    />
  );
}

function readViewport(): Viewport {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    dpr: Math.min(window.devicePixelRatio || 1, MAX_DPR),
  };
}

function createCanvas(container: HTMLElement): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.style.cssText =
    "position:absolute;inset:0;width:100%;height:100%;display:block;opacity:0;transition:opacity 1.4s cubic-bezier(0.22,1,0.36,1)";
  container.appendChild(canvas);
  return canvas;
}

function reveal(canvas: HTMLCanvasElement) {
  requestAnimationFrame(() => {
    canvas.style.opacity = "1";
  });
}

function createSink(container: HTMLElement, lands: readonly Land[], reduced: boolean): Sink {
  const viewport = readViewport();
  const canvas = createCanvas(container);

  if ("transferControlToOffscreen" in canvas && typeof Worker !== "undefined") {
    return workerSink(container, canvas, viewport, lands, reduced);
  }
  return mainThreadSink(canvas, viewport, lands, reduced) ?? cssSink(container, lands);
}

function workerSink(
  container: HTMLElement,
  canvas: HTMLCanvasElement,
  viewport: Viewport,
  lands: readonly Land[],
  reduced: boolean,
): Sink {
  const offscreen = canvas.transferControlToOffscreen();
  const worker = new Worker(new URL("./landscape.worker.ts", import.meta.url), { type: "module" });
  const state = { lands, viewport, reduced, visible: !document.hidden };
  let fallback: Sink | null = null;

  const post = (message: ToRenderer, transfer: Transferable[] = []) =>
    worker.postMessage(message, transfer);

  // WebGL2 is missing in workers on some browsers: retry on the main thread.
  const fallBack = () => {
    if (fallback) return;
    worker.terminate();
    canvas.remove();
    fallback =
      mainThreadSink(createCanvas(container), state.viewport, state.lands, state.reduced) ??
      cssSink(container, state.lands);
    fallback.visible(state.visible);
  };
  worker.onmessage = (event: MessageEvent<FromRenderer>) => {
    if (event.data.type === "ready") reveal(canvas);
    else fallBack();
  };
  worker.onerror = fallBack;

  post({ type: "init", canvas: offscreen, viewport, lands, reducedMotion: reduced }, [offscreen]);
  if (!state.visible) post({ type: "visible", visible: false });

  return {
    lands(next) {
      state.lands = next;
      if (fallback) fallback.lands(next);
      else post({ type: "lands", lands: next });
    },
    viewport(next) {
      state.viewport = next;
      if (fallback) fallback.viewport(next);
      else post({ type: "viewport", viewport: next });
    },
    visible(next) {
      state.visible = next;
      if (fallback) fallback.visible(next);
      else post({ type: "visible", visible: next });
    },
    reducedMotion(next) {
      state.reduced = next;
      if (fallback) fallback.reducedMotion(next);
      else post({ type: "reducedMotion", reducedMotion: next });
    },
    dispose() {
      worker.terminate();
      canvas.remove();
      fallback?.dispose();
    },
  };
}

function mainThreadSink(
  canvas: HTMLCanvasElement,
  viewport: Viewport,
  lands: readonly Land[],
  reduced: boolean,
): Sink | null {
  let engine: LandscapeEngine;
  try {
    engine = new LandscapeEngine(
      canvas,
      {
        request: (callback) => requestAnimationFrame(callback),
        cancel: (handle) => cancelAnimationFrame(handle),
      },
      () => reveal(canvas),
    );
  } catch {
    canvas.remove();
    return null;
  }
  engine.setViewport(viewport);
  engine.setReducedMotion(reduced);
  engine.setLands(lands);
  if (document.hidden) engine.setVisible(false);
  return {
    lands: (next) => engine.setLands(next),
    viewport: (next) => engine.setViewport(next),
    visible: (next) => engine.setVisible(next),
    reducedMotion: (next) => engine.setReducedMotion(next),
    dispose() {
      engine.stop();
      canvas.remove();
    },
  };
}

/** No WebGL at all: two gradient layers crossfaded with CSS opacity. */
function cssSink(container: HTMLElement, lands: readonly Land[]): Sink {
  const layers = [0, 1].map(() => {
    const layer = document.createElement("div");
    layer.style.cssText = "position:absolute;inset:0;opacity:0;transition:opacity 1s ease";
    container.appendChild(layer);
    return layer;
  });
  let front = 0;
  let shown: Land | null = null;
  const show = (next: readonly Land[]) => {
    const land = next[0] ?? "plains";
    if (land === shown) return;
    shown = land;
    front = 1 - front;
    layers[front]!.style.background = FALLBACK_GRADIENTS[land];
    layers[front]!.style.opacity = "1";
    layers[1 - front]!.style.opacity = "0";
  };
  show(lands);
  return {
    lands: show,
    viewport() {},
    visible() {},
    reducedMotion() {},
    dispose() {
      for (const layer of layers) layer.remove();
    },
  };
}

function devOverride(): readonly Land[] | null {
  if (!import.meta.env.DEV || typeof window === "undefined") return null;
  const lands = (new URLSearchParams(window.location.search).get("land") ?? "")
    .split(",")
    .filter(isLand);
  return lands.length > 0 ? lands : null;
}
